import {
  CheckDetails,
  ClaimRepayment,
  Lockbox,
  LockboxClaim,
  LockboxStatus,
  LockboxType,
  LockboxValidationError,
  Remainder,
} from '@/modules/employer/components/Lockbox/lockbox.types';
import { storeCreator } from '@/utils/store/store.utils';

export interface EditableLockbox {
  isUpdated?: boolean;
  lockboxId?: string;
  employeeId?: string;
  checkBenefitOf?: string;
  employeeName?: string;
  employeeAccountStatus?: string;
  employeeInvestmentsBalance?: number;
  employeeAvailableAmount?: number;
  isEmployeeFromSearch?: boolean;
  organizationId?: number;
  organizationPath?: string;
  vendorId?: string;
  accountId?: string;
  accountNumber?: string;
  amount?: string;
  lockboxType?: LockboxType;
  lockboxStatus?: LockboxStatus;
  reason?: string;
  notes?: string;
  liquidateAllFunds?: boolean;
  checkDetails?: CheckDetails;
  isMultipleRecordsWarning?: boolean;
  suggestedClaims?: LockboxClaim[];
  claimRepayments?: ClaimRepayment[];
  totalAllocated?: number;
  remainder?: Remainder;
  formName?: string;
  employeeSsn?: string;
  mailingType?: string;
  selectedCheckId?: string;
  selectedCheckType?: string;
}

interface State {
  editableLockbox?: EditableLockbox;
  setEditableLockbox: (value: Partial<EditableLockbox>) => void;
  originalLockbox?: Lockbox;
  setOriginalLockbox: (value: Partial<Lockbox>) => void;
  nextLockboxId?: string;
  setNextLockboxId: (value?: string) => void;
  validationErrors: LockboxValidationError[];
  setValidationErrors: (errors: LockboxValidationError[]) => void;
  pushValidationErrors: (errors: LockboxValidationError[]) => void;
  isEditMode?: boolean;
  setIsEditMode: (value: boolean) => void;
  reset: () => void;
  isDividingMode?: boolean;
  setIsDividingMode: (isDividingMode: boolean) => void;
}

export const useLockboxDetailsStore = storeCreator<State>((set) => ({
  editableLockbox: undefined,
  originalLockbox: undefined,
  isEditMode: undefined,
  nextLockboxId: undefined,
  isDividingMode: undefined,
  validationErrors: [],

  setEditableLockbox: (value: Partial<EditableLockbox>) => set((state) => ({
    ...state,
    editableLockbox: {
      ...state.editableLockbox,
      ...value,
    }
  })),

  setOriginalLockbox: (value: Partial<Lockbox>) => set((state) => ({
    ...state,
    originalLockbox: {
      ...state.originalLockbox,
      ...value,
    }
  })),

  setNextLockboxId: (value?: string) => set((state) => ({
    ...state,
    nextLockboxId: value,
  })),

  setValidationErrors: (errors: LockboxValidationError[]) => set((state) => ({
    ...state,
    validationErrors: errors,
  })),

  pushValidationErrors: (errors: LockboxValidationError[]) => set((state) => ({
    ...state,
    validationErrors: Array.from(new Set([...(state?.validationErrors || []), ...errors])),
  })),

  setIsEditMode: (value: boolean) => set(() => ({
    isEditMode: value,
  })),

  setIsDividingMode: (value: boolean) => set(() => ({
    isDividingMode: value,
  })),

  reset: () => set(() => ({
    editableLockbox: undefined,
    originalLockbox: undefined,
    isEditMode: undefined,
    isDividingMode: undefined,
    validationErrors: [],
  })),
}));

export default useLockboxDetailsStore;
